import * as joint from '@clientio/rappid';

import { ContainerAttributes } from './container-attributes';
import { CalculatedSize, ContainerResizeCalculator } from './ContainerResizeCalculator';
import { getHeightOfSubComponent } from './ContainerSubComponentCalculator';
import { FlowchartConfig } from '../../base/flowchart.config';

export class ContainerResize {
  public static createContainerResizingTool(cellView: joint.dia.CellView, paper: joint.dia.Paper) {
    //calculate size of tags to set min width for resize
    const subActions = (cellView.model as joint.dia.Element)
      .getEmbeddedCells(paper)
      .filter((el) => el.get(ContainerAttributes.IsSubComponent));
    let maxWidth = 0;
    subActions.forEach((embed) => {
      const tags = embed.getEmbeddedCells().filter((e) => e.attributes.isElementTag);
      let tagWidth = 0;
      tags.forEach((tag) => {
        tagWidth += (tag as joint.dia.Element).attributes.size.width + FlowchartConfig.tag.padding.paddingBetweenTags;
      });
      if (tagWidth > maxWidth) {
        maxWidth = tagWidth;
      }
    });

    //add paddings
    maxWidth += FlowchartConfig.tag.padding.horizontalPadding + 2 * FlowchartConfig.containerNode.subComponentNode.styles.horizontalPadding;

    return new joint.ui.FreeTransform({
      cellView: cellView,
      minWidth: maxWidth > FlowchartConfig.containerNode.minSize.width ? maxWidth : FlowchartConfig.containerNode.minSize.width,
      minHeight: ContainerResize.getMinContainerHeight(cellView.model as joint.dia.Element, paper),
      maxHeight: Infinity,
      resizeGrid: { width: FlowchartConfig.paper.gridSize * 2, height: FlowchartConfig.paper.gridSize * 2 },
      resizeDirections: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
      allowRotation: false,
      theme: 'material',
    });
  }

  private static getMinContainerHeight(containerElement: joint.dia.Element, paper: joint.dia.Paper): number {
    const subActions = containerElement.getEmbeddedCells(paper).filter((el) => el.get(ContainerAttributes.IsSubComponent));
    if (this.isContainerGenerated(containerElement) && this.doesContainerHaveSubActions(containerElement)) {
      return ContainerResizeCalculator.calculateContainerHeightForEmbeds(subActions as joint.dia.Element[]);
    } else {
      return FlowchartConfig.containerNode.size.height;
    }
  }

  public static adjustContainerSizeOnEmbedChange(containerElement: joint.dia.Element, paper: joint.dia.Paper) {
    const newSize: joint.dia.Size = containerElement.size();
    this.adjustContainerSize(containerElement, newSize, paper);
  }

  public static repositionContainerElements(containerElement: joint.dia.Element, paper: joint.dia.Paper, hideArrows = false) {
    const containerPosition = containerElement.attributes.position;
    const { subComponentNode } = FlowchartConfig.containerNode;
    containerElement.getEmbeddedCells(paper);
    const subActions = containerElement.getEmbeddedCells(paper).filter((el) => el.get(ContainerAttributes.IsSubComponent));
    const selectionBehaviors = containerElement.getEmbeddedCells(paper).filter((el) => el.get(ContainerAttributes.IsSelectionBehavior));

    let height = subComponentNode.styles.verticalPaddingBaseTop;
    subActions.forEach((embed, subActionIndex) => {
      const embedElement = embed as joint.dia.Element;
      const selectionBehaviorElement = selectionBehaviors[subActionIndex] as joint.dia.Element;

      embedElement.resize(
        containerElement.attributes.size.width - FlowchartConfig.containerNode.subComponentNode.offset,
        getHeightOfSubComponent(embedElement),
      );

      embedElement.position(containerPosition.x + subComponentNode.styles.horizontalPadding, containerPosition.y + height);

      const tags = embed.getEmbeddedCells().filter((e) => e.attributes.isElementTag);
      const description = embed.getEmbeddedCells().filter((e) => e.attributes.isElementDescription);

      if (tags) {
        tags.forEach((tag) => {
          (tag as joint.dia.Element).position(
            (tag as joint.dia.Element).attributes.position.x,
            embedElement.attributes.position.y + FlowchartConfig.tag.padding.verticalPadding,
          );
        });
      }

      if (description) {
        description.forEach((desc) => {
          (desc as joint.dia.Element).position(
            (desc as joint.dia.Element).attributes.position.x,
            tags ? embedElement.attributes.position.y + 55 : embedElement.attributes.position.y + 45,
          );
        });
      }

      if (selectionBehaviorElement) {
        const newSelectionBehaviorX =
          containerElement.position().x + containerElement.size().width / 2 - selectionBehaviorElement.size().width / 2;
        selectionBehaviorElement.position(newSelectionBehaviorX, embedElement.attributes.position.y + embedElement.attributes.size.height);
      }

      const subActionArrows = embed.getEmbeddedCells().filter((el) => el.get(ContainerAttributes.IsSubComponentArrow));
      if (subActionArrows.length > 0) {
        subActionArrows.forEach((arrow: joint.dia.Element) => {
          const arrowView = paper.findViewByModel(arrow);
          if (hideArrows) {
            arrowView.$el.hide();
          }
          if (arrow.attributes[ContainerAttributes.IsSubComponentUpArrow]) {
            let position;
            if (subActionIndex === subActions.length - 1) {
              position = this.getSubComponentArrowRightPosition(embedElement, arrow);
            } else {
              position = this.getSubComponentArrowLeftPosition(embedElement, arrow);
            }

            arrow.position(position.x, position.y);
          } else {
            const position = this.getSubComponentArrowRightPosition(embedElement, arrow);
            arrow.position(position.x, position.y);
          }
        });
      }

      height += (embed as joint.dia.Element).attributes.size.height + subComponentNode.styles.verticalPadding;
    });
  }

  private static getSubComponentArrowLeftPosition(embedElement: joint.dia.Element, arrow: joint.dia.Element): { x: number; y: number } {
    return {
      x:
        embedElement.position().x +
        embedElement.size().width -
        FlowchartConfig.containerNode.subComponentNode.arrow.width * 2 -
        FlowchartConfig.containerNode.subComponentNode.arrow.marginBetweenParent -
        FlowchartConfig.containerNode.subComponentNode.arrow.marginBetweenArrows,
      y: embedElement.attributes.position.y + embedElement.attributes.size.height / 2 - arrow.size().height / 2,
    };
  }

  public static getSubComponentArrowRightPosition(embedElement: joint.dia.Element, arrow: joint.dia.Element): { x: number; y: number } {
    return {
      x:
        embedElement.position().x +
        embedElement.size().width -
        FlowchartConfig.containerNode.subComponentNode.arrow.width -
        FlowchartConfig.containerNode.subComponentNode.arrow.marginBetweenParent,
      y: embedElement.attributes.position.y + embedElement.attributes.size.height / 2 - arrow.size().height / 2,
    };
  }

  public static adjustContainerSizeOnResize(containerElement: joint.dia.Element, newSize: joint.dia.Size, paper: joint.dia.Paper) {
    this.adjustContainerSize(containerElement, newSize, paper);
  }

  private static isContainerGenerated(containerElement: joint.dia.Element): boolean {
    return !!containerElement.get(ContainerAttributes.HasHeader);
  }

  private static doesContainerHaveSubActions(containerElement: joint.dia.Element): boolean {
    return !!containerElement.get(ContainerAttributes.HasSubAction);
  }

  public static adjustContainerSize(containerElement: joint.dia.Element, newSize: joint.dia.Size, paper: joint.dia.Paper) {
    if (newSize.height > this.getMinContainerHeight(containerElement, paper)) {
      this.snapContainerSizeToGrid(containerElement, newSize);
    } else if (this.isContainerGenerated(containerElement)) {
      this.fitContainerToEmbeds(containerElement, paper);
    } else {
      this.resetContainerSize(containerElement);
    }
  }

  private static fitContainerToEmbeds(containerElement: joint.dia.Element, paper: joint.dia.Paper) {
    const subActions = containerElement.getEmbeddedCells(paper).filter((el) => el.get(ContainerAttributes.IsSubComponent));

    const sizeToGrid = this.getSizeToGrid({
      width: containerElement.attributes.size.width,
      height: ContainerResizeCalculator.calculateContainerHeightForEmbeds(subActions as joint.dia.Element[]),
    });

    containerElement.resize(sizeToGrid.width, sizeToGrid.height);
  }

  public static adjustWidthOfContainer(containerElement: joint.dia.Element, paper: joint.dia.Paper) {
    const subActions = containerElement.getEmbeddedCells(paper).filter((el) => el.get(ContainerAttributes.IsSubComponent));

    containerElement.resize(
      ContainerResizeCalculator.getMaxWidthOfSubElementWithPadding(subActions),
      containerElement.attributes.size.height,
    );
  }

  private static resetContainerSize(containerElement: joint.dia.Element) {
    containerElement.resize(FlowchartConfig.containerNode.size.width, FlowchartConfig.containerNode.size.height);
  }

  private static snapContainerSizeToGrid(containerElement: joint.dia.Element, newSize: joint.dia.Size) {
    const updatedSize = ContainerResize.getSizeToGrid(newSize);
    containerElement.resize(updatedSize.width, updatedSize.height);
  }

  public static getSizeToGrid(size: joint.dia.Size): joint.dia.Size {
    const gridSnappedSize = ContainerResizeCalculator.calculateSizeToGrid(size as CalculatedSize);

    return gridSnappedSize as joint.dia.Size;
  }

  public static isAllSubActionsRemoved(containerElement: joint.dia.Element): boolean {
    const hasSubComponents = containerElement.getEmbeddedCells().filter((el) => el.attributes.isContainerSubComponent)?.length > 0;
    return ContainerResize.isContainerGenerated(containerElement) && !hasSubComponents;
  }
}
